import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { shortDate } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, ArrowUpDown, Plus, Send, Wallet, Download, Sparkles, ChevronDown, ChevronRight, Eye, LogIn, Building2, TrendingDown } from 'lucide-react';
import { generateIntelReport } from '@/lib/workerApi';
import { startImpersonation } from '@/lib/impersonation';
import { logUpdate, logCreate } from '@/lib/audit-log';
import { usd } from '@/lib/refunds';
import { DataPagination } from '@/components/DataPagination';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import type { DateRangeValue } from '@/components/DateRangeFilter';
import { GrantCreditDialog, type GrantCreditTarget } from '@/components/GrantCreditDialog';
import { DeductCreditDialog, type DeductCreditTarget } from '@/components/DeductCreditDialog';
import { fetchCreditGrantContext } from '@/lib/adminCredits';

// Derives onboarding progress from brand_configs fields (there is no explicit
// "current step" column). Mirrors the 3-step flow in Brand-Dashboard's Onboarding.tsx:
//   Step 1 (Basics):  brand_name + website_url + category + logo_url
//   Step 2 (Plan):     a plan was committed (paid sub, onboarding finished, or step 3 reached)
//   Step 3 (Intel):    products + product_description + product_keywords + delivery_email
function getOnboardingSteps(bc: any) {
  const step1 = !!(bc?.brand_name && bc?.website_url && bc?.category && bc?.logo_url);
  const step3 = !!(
    bc?.products?.length &&
    bc?.product_description &&
    bc?.product_keywords?.length &&
    bc?.delivery_email
  );
  const step2 = !!(bc?.whop_subscription_id || bc?.onboarding_completed || step3);
  return { step1, step2, step3, done: [step1, step2, step3].filter(Boolean).length };
}

// Renders a brand_user_access member list for a brand's "Team Members" expand.
// Agency-owned brands are shown on the separate Agencies page — this Brands
// page only lists standalone/solo brands and their teams.
function MemberGrid({ members, emptyLabel, onImpersonate }: {
  members: any[];
  emptyLabel: string;
  /** Opens the "switch as user" confirmation for one team member. */
  onImpersonate?: (member: any) => void;
}) {
  if (members.length === 0) {
    return <p className="text-sm text-muted-foreground italic">{emptyLabel}</p>;
  }
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {members.map((m: any) => (
        <div
          key={m.id}
          className="flex items-center gap-3 rounded-lg border bg-background/80 p-3 shadow-sm transition-shadow hover:shadow-md"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
            {(m.profile?.full_name || m.profile?.email || m.email || '?').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{m.profile?.full_name || m.profile?.email || m.email || '–'}</p>
            <p className="truncate text-xs text-muted-foreground">{m.profile?.email || m.email || '–'}</p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            {m.is_owner && <Badge className="text-[10px]">Owner</Badge>}
            <Badge variant="outline" className="capitalize text-[10px]">{m.role || '–'}</Badge>
          </div>
          {onImpersonate && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 shrink-0 p-0"
              disabled={!m.user_id}
              title={m.user_id
                ? 'Switch as user (sign in as this team member)'
                : 'No login on file for this member'}
              onClick={() => onImpersonate(m)}
            >
              <LogIn className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

// Live wallet balance + a Grant button for the brand detail sheet. Its own
// query (rather than reading the row's cached brand_config.account_credit_cents)
// because it needs the SAME admin_credit_grant_context() read the grant dialog
// uses — that's what surfaces "you're a member of this brand" and the admin's
// own remaining headroom, not just the number.
function BrandCreditSummary({ brandId, onGrant, onDeduct }: {
  brandId: string | null; onGrant: () => void; onDeduct: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['credit-grant-context', brandId],
    queryFn: () => fetchCreditGrantContext(brandId),
    enabled: !!brandId,
  });
  const b = data?.brand;

  if (!brandId) {
    return <p className="text-xs text-muted-foreground italic">No brand account yet — nothing to credit.</p>;
  }

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 p-3">
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">Wallet balance</p>
        <p className="text-lg font-semibold tabular-nums">
          {isLoading ? '…' : b ? usd(b.balance_cents) : '—'}
        </p>
        {b && b.lifetime_granted_cents > 0 && (
          <p className="truncate text-[11px] text-muted-foreground">
            {usd(b.lifetime_granted_cents)} granted by BMC to date
          </p>
        )}
      </div>
      <div className="flex shrink-0 gap-2">
        <Button
          size="sm"
          variant="outline"
          className="border-violet-300 text-violet-700 hover:bg-violet-50"
          onClick={onGrant}
        >
          <Wallet className="mr-1.5 h-3.5 w-3.5" /> Grant
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="border-red-300 text-red-700 hover:bg-red-50"
          onClick={onDeduct}
        >
          <TrendingDown className="mr-1.5 h-3.5 w-3.5" /> Deduct
        </Button>
      </div>
    </div>
  );
}

export default function BrandUsers() {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<any | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [planTierFilter, setPlanTierFilter] = useState<'all' | 'free' | 'pro' | 'pro_annual'>('all');
  const [dateRange, setDateRange] = useState<DateRangeValue | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const brandPortal =
      import.meta.env.VITE_ENV === "development"
        ? "http://localhost:5173"
        : process.env.VITE_ENV === "staging"
          ? "app.brandsmeetcreators.tech"
          : "app.brandsmeetcreators.com";

  // Pagination for wallet transactions in detail sheet
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsPageSize, setTransactionsPageSize] = useState(10);

  // Invite modal
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteCredits, setInviteCredits] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  // Wallet modal
  const [walletBrand, setWalletBrand] = useState<any | null>(null);
  const [walletAmount, setWalletAmount] = useState('');
  const [walletReason, setWalletReason] = useState('');
  const [isAddingFunds, setIsAddingFunds] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [confirmGenerateBrand, setConfirmGenerateBrand] = useState<any | null>(null);

  // Impersonation ("switch as user")
  const [confirmImpersonateBrand, setConfirmImpersonateBrand] = useState<any | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  // BMC-managed toggle — see brand_configs.bmc_managed. Confirmed before
  // writing because flipping it on holds every one of this brand's videos
  // out of the 7-day auto-approve sweep indefinitely, not a cosmetic label.
  const [pendingBmcManagedChange, setPendingBmcManagedChange] = useState<{ id: string; name: string; next: boolean } | null>(null);
  const [isSavingBmcManaged, setIsSavingBmcManaged] = useState(false);

  // Grant wallet credit — real money, gated server-side on the acting admin
  // holding credits.grant (see lib/adminCredits.ts + GrantCreditDialog).
  const [creditBrand, setCreditBrand] = useState<GrantCreditTarget | null>(null);
  // Deduct wallet credit — the removal counterpart, gated on the SEPARATE
  // credits.deduct permission (see lib/adminCredits.ts + DeductCreditDialog).
  // An admin who can grant credit does not automatically get this button's
  // action to succeed — the dialog itself checks and explains why.
  const [deductBrand, setDeductBrand] = useState<DeductCreditTarget | null>(null);

  const handleGenerateIntelReport = async () => {
    if (!confirmGenerateBrand?.brand_id) return;
    const brandName = confirmGenerateBrand.brand_config?.brand_name || confirmGenerateBrand.brand_name || 'Brand';
    const { data: { session } } = await supabase.auth.getSession();
    const authToken = session?.access_token;
    generateIntelReport(confirmGenerateBrand.brand_id, authToken)
      .then(result => {
        logCreate('brand_briefs', confirmGenerateBrand.brand_id, {
          brief_id: result.id,
          total_videos_analyzed: result.total_videos_analyzed,
          generated_at: result.generated_at,
        }, {
          status: 'success',
          brand_name: brandName,
          triggered_by: 'admin',
        });
      })
      .catch(err => {
        const errorMessage = err?.message || 'Unknown error';
        toast.error(`Intel report failed for ${brandName}: ${errorMessage}`);
        logCreate('brand_briefs', confirmGenerateBrand.brand_id, {}, {
          status: 'failed',
          brand_name: brandName,
          triggered_by: 'admin',
          error: errorMessage,
        });
      });
    toast.success(`Intel report generation started for ${brandName}`);
    setConfirmGenerateBrand(null);
  };

  // Generate a one-time Supabase magic link (server-side, service role) and open
  // the brand dashboard signed in as this brand. Admin-only — the API gates it on
  // the admin's Supabase JWT (app_metadata.is_admin).
  const handleImpersonate = async () => {
    const b = confirmImpersonateBrand;
    if (!b) return;
    setIsImpersonating(true);
    try {
      const brandName = b.brand_config?.brand_name || b.brand_name || b.email || 'brand';
      await startImpersonation({
        userId: b.profile_id,
        email: b.email,
        role: 'brand',
        auditTable: 'brand_impersonation',
        auditValues: { brand_name: brandName },
      });
      toast.success(`Opening the dashboard as ${brandName}…`);
      setConfirmImpersonateBrand(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to start impersonation');
    } finally {
      setIsImpersonating(false);
    }
  };

  // Switching into a TEAM MEMBER rather than the brand owner. Same endpoint —
  // a member is an ordinary Supabase auth user — and the session lands with that
  // member's own brand_user_access row, so an admin sees the brand exactly as
  // that person's role sees it (an editor's or viewer's view, not the owner's).
  const [confirmImpersonateMember, setConfirmImpersonateMember] = useState<
    { member: any; brandName: string } | null
  >(null);
  const [isImpersonatingMember, setIsImpersonatingMember] = useState(false);

  const handleImpersonateMember = async () => {
    if (!confirmImpersonateMember) return;
    const { member, brandName } = confirmImpersonateMember;
    setIsImpersonatingMember(true);
    try {
      const memberName = member.profile?.full_name || member.profile?.email || 'team member';
      await startImpersonation({
        userId: member.user_id,
        email: member.profile?.email,
        role: 'brand',
        auditTable: 'brand_member_impersonation',
        auditValues: {
          brand_name: brandName,
          brand_id: member.brand_id || null,
          brand_role: member.role || null,
          is_owner: !!member.is_owner,
        },
      });
      toast.success(`Opening the dashboard as ${memberName}…`);
      setConfirmImpersonateMember(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to start impersonation');
    } finally {
      setIsImpersonatingMember(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setCurrentPage(1); }, [activeFilter]);
  useEffect(() => { setCurrentPage(1); }, [planTierFilter]);

  const { data: brandsData, isLoading } = useQuery({
    queryKey: ['brand-users', sortField, sortAsc, search, currentPage, pageSize, activeFilter, planTierFilter, dateRange],
    queryFn: async () => {
      const q = search.trim();

      const fetchIntelAndPayments = async (brandIds: string[]) => {
        const intelSummaryMap = new Map<string, { successCount: number; last_at: string; last_status: string; last_error: string | null }>();
        const subscriptionStartMap = new Map<string, string>();
        // Number of hiring campaigns ("jobs") per brand. Keyed by brand_id
        // (= brand_configs.id); team members share the owner's brand_id, so this
        // count covers the whole brand regardless of who created each campaign.
        const campaignCountMap = new Map<string, number>();
        if (brandIds.length === 0) return { intelSummaryMap, subscriptionStartMap, campaignCountMap };

        const [intelRes, paymentRes, campaignRes] = await Promise.all([
          supabase.rpc('get_brand_intel_summary', { p_brand_ids: brandIds }),
          supabase
            .from('brand_subscription_payments')
            .select('brand_id, paid_at')
            .in('brand_id', brandIds)
            .eq('status', 'succeeded')
            .order('paid_at', { ascending: false }),
          supabase.rpc('get_brand_campaign_counts', { p_brand_ids: brandIds }),
        ]);

        for (const row of intelRes.data ?? []) {
          intelSummaryMap.set(row.brand_id, {
            successCount: Number(row.success_count),
            last_at: row.last_at,
            last_status: row.last_status ?? 'unknown',
            last_error: row.last_error ?? null,
          });
        }
        for (const row of paymentRes.data ?? []) {
          if (!subscriptionStartMap.has(row.brand_id)) {
            subscriptionStartMap.set(row.brand_id, row.paid_at);
          }
        }
        for (const row of campaignRes.data ?? []) {
          campaignCountMap.set(row.brand_id, Number(row.campaign_count));
        }
        return { intelSummaryMap, subscriptionStartMap, campaignCountMap };
      };

      // admin_list_brands (RPC) unions two sources, grouped so pagination is
      // correct at the "one row per brand" level. Agency-owned brands are
      // excluded here — they live on the separate Agencies page:
      //   - 'brand' — a standalone (non-agency) brand_configs row, represented
      //     by its owner (or earliest member) — team members never split into
      //     extra rows
      //   - 'pending' — a role='brand' profile with NO brand_user_access row
      //     at all (signed up, hasn't done onboarding step 1 yet — anchoring
      //     on brand_configs alone would silently drop these)
      const { data: rpcRows, error: rpcErr } = await supabase.rpc('admin_list_brands', {
        p_search: q || null,
        p_active: activeFilter === 'all' ? null : activeFilter === 'active',
        p_plan_tier: planTierFilter === 'all' ? null : planTierFilter,
        p_date_from: dateRange ? dateRange.from.toISOString() : null,
        p_date_to: dateRange ? dateRange.to.toISOString() : null,
        p_sort_asc: sortAsc,
        p_limit: pageSize,
        p_offset: (currentPage - 1) * pageSize,
      });
      if (rpcErr) throw rpcErr;
      if (!rpcRows || rpcRows.length === 0) return { data: [], total: 0 };

      const total = Number(rpcRows[0]?.total_count ?? 0);
      const brandIds = rpcRows.map((r: any) => r.brand_id).filter(Boolean);

      // Full team-member list per brand, for the expand dropdown — the RPC
      // only returns the representative (owner) row plus a member_count.
      const { data: accessRows } = brandIds.length > 0
        ? await supabase
            .from('brand_user_access')
            .select('*')
            .in('brand_id', brandIds)
            .order('is_owner', { ascending: false })
            .order('created_at', { ascending: true })
        : { data: [] };

      const memberUserIds = [...new Set((accessRows ?? []).map((a: any) => a.user_id).filter(Boolean))];
      const { data: memberProfiles } = memberUserIds.length > 0
        ? await supabase.from('profiles').select('*').in('id', memberUserIds)
        : { data: [] };
      const memberProfileById = new Map((memberProfiles ?? []).map((p: any) => [p.id, p]));

      const membersByBrandId = new Map<string, any[]>();
      for (const a of accessRows ?? []) {
        const list = membersByBrandId.get(a.brand_id) ?? [];
        list.push({ ...a, profile: memberProfileById.get(a.user_id) ?? null });
        membersByBrandId.set(a.brand_id, list);
      }

      const { intelSummaryMap, subscriptionStartMap, campaignCountMap } = await fetchIntelAndPayments(brandIds);

      const data = rpcRows.map((r: any) => {
        const bc = r.brand_config;
        return {
          id: r.entity_id,
          brand_id: r.brand_id,
          profile_id: r.profile_id,
          is_pending: r.entity_type === 'pending',
          brand_name: bc?.brand_name ?? null,
          email: r.email,
          created_at: r.created_at,
          access_id: r.access_id,
          wallet_balance: null,
          brand_config: bc,
          members: r.brand_id ? (membersByBrandId.get(r.brand_id) ?? []) : [],
          intel_summary: r.brand_id ? intelSummaryMap.get(r.brand_id) ?? null : null,
          subscription_start: r.brand_id ? subscriptionStartMap.get(r.brand_id) ?? null : null,
          job_count: r.brand_id ? campaignCountMap.get(r.brand_id) ?? 0 : 0,
        };
      });

      return { data, total };
    },
  });

  const brands = brandsData?.data ?? [];
  const totalBrands = brandsData?.total ?? 0;
  const totalPages = Math.ceil(totalBrands / pageSize);

  const { data: walletTransactionsData } = useQuery({
    queryKey: ['brand-wallet-transactions', selectedBrand?.access_id, transactionsPage, transactionsPageSize],
    enabled: !!selectedBrand?.access_id,
    queryFn: async () => {
      const { data, count, error } = await supabase
        .from('wallet_transactions')
        .select('*', { count: 'exact' })
        .eq('brand_id', selectedBrand.access_id)
        .order('created_at', { ascending: false })
        .range((transactionsPage - 1) * transactionsPageSize, transactionsPage * transactionsPageSize - 1);
      if (error) throw error;
      return { data: data ?? [], total: count ?? 0 };
    },
  });

  const walletTransactions = walletTransactionsData?.data ?? [];
  const totalTransactions = walletTransactionsData?.total ?? 0;
  const totalTransactionsPages = Math.ceil(totalTransactions / transactionsPageSize);

  const handleInvite = async () => {
    if (!inviteEmail) return;
    setIsInviting(true);
    try {
      const initialCredits = Number(inviteCredits) || 0;
      const { error: authError } = await supabase.auth.admin.inviteUserByEmail(inviteEmail, { initialCredits });
      if (authError) {
        const msg = authError.message || authError.code || 'Unknown auth error';
        throw new Error(msg);
      }

      toast.success(`✅ Magic link sent to ${inviteEmail}`);
      queryClient.invalidateQueries({ queryKey: ['brand-users'] });
      setInviteOpen(false);
      setInviteEmail('');
      setInviteCredits('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to invite brand');
    } finally {
      setIsInviting(false);
    }
  };

  const handleAddFunds = async () => {
    if (!walletBrand || !walletAmount || Number(walletAmount) <= 0 || Number(walletAmount) > 10000) return;
    setIsAddingFunds(true);
    try {
      const amount = Number(walletAmount);
      const currentBalance = walletBrand.wallet_balance ?? 0;
      const newBalance = currentBalance + amount;
      
      const { data, error } = await supabase
        .from('brand_user_access')
        .update({ wallet_balance: newBalance })
        .eq('id', walletBrand.access_id)
        .select()
        .single();
      if (error) throw error;

      await logUpdate('brands', walletBrand.id,
        { wallet_balance: currentBalance },
        { wallet_balance: newBalance },
        {
          description: `Admin added $${amount} to wallet`,
          reason: walletReason || 'Manual admin deposit',
          previousBalance: currentBalance,
          amountAdded: amount,
        }
      );

      // Log to wallet_transactions if the table exists
      try {
        await supabase.from('wallet_transactions').insert({
          brand_id: walletBrand.access_id,
          amount,
          type: 'admin_deposit',
          created_at: new Date().toISOString(),
        });
      } catch (txErr) {
        console.warn('Wallet transaction log failed (non-fatal):', txErr);
      }

      toast.success(`✅ $${amount} added to ${walletBrand.brand_name}'s wallet`);
      
      // Optimistically update cached brands data without refetching
      queryClient.setQueryData(
        ['brand-users', sortField, sortAsc, search, currentPage, pageSize],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((brand: any) =>
              brand.id === walletBrand.id ? { ...brand, wallet_balance: newBalance } : brand
            ),
          };
        }
      );
      
      // Update wallet transactions in detail sheet
      queryClient.invalidateQueries({ queryKey: ['brand-wallet-transactions'] });
      
      setWalletBrand(null);
      setWalletAmount('');
      setWalletReason('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to add funds');
    } finally {
      setIsAddingFunds(false);
    }
  };

  // BMC-managed toggle — see brand_configs.bmc_managed. brand_configs.id IS
  // the brand's id (bc.id below), same id the worker's 7-day sweep reads via
  // cr_hires.brand_id, so no translation is needed between what this writes
  // and what the sweep checks.
  const handleSetBmcManaged = async () => {
    if (!pendingBmcManagedChange) return;
    const { id, next } = pendingBmcManagedChange;
    setIsSavingBmcManaged(true);
    try {
      const { data: oldData } = await supabase.from('brand_configs').select('bmc_managed').eq('id', id).single();
      const { error } = await supabase.from('brand_configs').update({ bmc_managed: next }).eq('id', id);
      if (error) throw error;

      await logUpdate('brand_configs', id,
        { bmc_managed: oldData?.bmc_managed ?? false },
        { bmc_managed: next },
        { description: next ? 'Enabled BMC-managed review (holds the 7-day video auto-approve)' : 'Disabled BMC-managed review' }
      );

      toast.success(next ? 'BMC-managed review turned on — videos hold for manual review' : 'BMC-managed review turned off — the normal 7-day auto-approve resumes');
      // The switch lives in the table, so the list read is what has to catch
      // up — invalidate rather than patching a cache key that carries every
      // filter/sort/page in it.
      queryClient.invalidateQueries({ queryKey: ['brand-users'] });
      setPendingBmcManagedChange(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update BMC-managed status');
    } finally {
      setIsSavingBmcManaged(false);
    }
  };

  const toggleSort = (field: string) => {
    setCurrentPage(1);
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(true); }
  };

  const handlePageSizeChange = (newSize: number) => {
    setCurrentPage(1);
    setPageSize(newSize);
  };

  const SortHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort(field)}>
      <div className="flex items-center gap-1">{children}<ArrowUpDown className="h-3 w-3" /></div>
    </TableHead>
  );

  const handleExportBrands = async () => {
    setIsExporting(true);
    try {
      // Step 1: All brand profiles
      const { data: exportProfiles, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'brand')
        .order('created_at', { ascending: sortAsc });

      if (error) throw error;
      if (!exportProfiles || exportProfiles.length === 0) {
        toast.error('No brands available to export');
        return;
      }

      // Step 2: brand_user_access for these profiles
      const exportProfileIds = exportProfiles.map((p: any) => p.id);
      const { data: exportAccessRows } = await supabase
        .from('brand_user_access')
        .select('*')
        .in('user_id', exportProfileIds);

      const exportAccessByUserId = new Map<string, any>();
      for (const a of exportAccessRows ?? []) {
        exportAccessByUserId.set(a.user_id, a);
      }

      // Step 3: brand_configs
      const exportBrandIds = (exportAccessRows ?? []).map((a: any) => a.brand_id).filter(Boolean);
      const exportBrandConfigMap = new Map<string, any>();
      if (exportBrandIds.length > 0) {
        const { data: exportConfigs } = await supabase
          .from('brand_configs')
          .select('*')
          .in('id', exportBrandIds);
        for (const bc of exportConfigs ?? []) {
          exportBrandConfigMap.set(bc.id, bc);
        }
      }

      const enrichedData = exportProfiles.map((p: any) => {
        const access = exportAccessByUserId.get(p.id);
        const brandConfig = access ? exportBrandConfigMap.get(access.brand_id) : null;
        return { ...(access ?? {}), ...p, brand_config: brandConfig ? JSON.stringify(brandConfig) : '' };
      });

      const columns: string[] = Array.from(
        enrichedData.reduce((keys, row) => {
          Object.keys(row).forEach((key) => keys.add(key));
          return keys;
        }, new Set<string>())
      );

      const escapeCsvValue = (value: any) => {
        const stringValue = value == null
          ? ''
          : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
      };

      const csvRows = [
        columns.join(','),
        ...enrichedData.map((row) => columns.map((column) => escapeCsvValue(row[column])).join(',')),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `brand-users-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${exportData?.length} brands`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to export brands');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by email..." value={searchInput} onChange={e => setSearchInput(e.target.value)} className="pl-9" />
          </div>
          <Select value={activeFilter} onValueChange={(v) => setActiveFilter(v as 'all' | 'active' | 'inactive')}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={planTierFilter} onValueChange={(v) => setPlanTierFilter(v as typeof planTierFilter)}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Plan Tier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tiers</SelectItem>
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
              <SelectItem value="pro_annual">Pro Annual</SelectItem>
            </SelectContent>
          </Select>
          <DateRangeFilter
            value={dateRange}
            onChange={(r) => { setCurrentPage(1); setDateRange(r); }}
            placeholder="Select Date Range"
          />
        </div>
        {/* <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExportBrands} disabled={isExporting || !brands || brands?.length === 0}>
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
          <Button onClick={() => setInviteOpen(true)} className="gap-2">
            <Send className="h-4 w-4" /> Invite Brand
          </Button>
        </div> */}
      </div>

      <Card>
        <CardContent>
          {/* Height cap goes on the Table's own scroll wrapper — nesting a
              second overflow div puts the horizontal scrollbar below the
              clipped rows, out of reach.
              Below sm the cap is lifted: a phone draws overlay scrollbars, so
              a nested vertical scroller paints its bar on top of the rightmost
              cells. The page owns vertical scrolling there and the table keeps
              only the horizontal axis. */}
          <Table containerClassName="max-h-[600px] max-sm:max-h-none">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Brand Name</TableHead>
                  <TableHead>Email</TableHead>
                  {/* Sits this far left on purpose: it is the one column here
                      an admin ACTS on rather than reads, and it was missed
                      entirely while it lived inside the detail drawer. */}
                  <TableHead
                    className="whitespace-nowrap"
                    title="BMC-managed brands are reviewed by hand — their delivered videos never hit the 7-day auto-approve."
                  >
                    BMC Managed
                  </TableHead>
                  {/* <TableHead>Active</TableHead> */}
                  <TableHead>Plan Tier</TableHead>
                  <TableHead>Plan Status</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-center">Jobs</TableHead>
                  <TableHead>Steps</TableHead>
                  <TableHead>Shop</TableHead>
                  <TableHead>Last Subscription</TableHead>
                  <SortHeader field="created_at">Created</SortHeader>
                  <TableHead>Last Report</TableHead>
                  <TableHead>Reports</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow><TableCell colSpan={15} className="text-center text-muted-foreground">Loading...</TableCell></TableRow>
                )}
                {brands?.flatMap((b: any) => {
                  const bc = b.brand_config;
                  const steps = getOnboardingSteps(bc);
                  const isExpanded = expandedId === b.id;
                  const rows = [
                    <TableRow key={b.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setExpandedId(isExpanded ? null : b.id)}>
                      <TableCell>{isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</TableCell>
                      <TableCell className="font-medium">
                        {bc?.brand_name || b.brand_name || (
                          <span className="text-muted-foreground italic">
                            {b.is_pending ? 'Pending signup' : '–'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{b.email || bc?.contact_email || bc?.delivery_email || '–'}</TableCell>
                      {/* stopPropagation: the row itself expands on click, and
                          a toggle that also expanded the row would read as a
                          misfire. */}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {bc?.id ? (
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={bc.bmc_managed === true}
                              onCheckedChange={(v) => setPendingBmcManagedChange({
                                id: bc.id,
                                name: bc.brand_name || b.brand_name,
                                next: v,
                              })}
                            />
                            <span className={`text-xs whitespace-nowrap ${bc.bmc_managed === true ? 'font-semibold text-amber-700' : 'text-muted-foreground'}`}>
                              {bc.bmc_managed === true ? 'Held' : 'Auto'}
                            </span>
                          </div>
                        ) : (
                          // A pending signup has no brand_configs row to flag.
                          <span className="text-muted-foreground" title="No brand record yet — this signup hasn't onboarded">–</span>
                        )}
                      </TableCell>
                      {/* <TableCell>
                        {bc != null
                          ? <Badge variant={bc.is_active === true ? 'default' : 'secondary'}>{bc.is_active === true ? 'Active' : 'Inactive'}</Badge>
                          : <span className="text-muted-foreground">–</span>}
                      </TableCell> */}
                      <TableCell><Badge variant="outline" className="capitalize">{bc?.plan_tier || '–'}</Badge></TableCell>
                      <TableCell>
                        {b.is_pending
                          ? <Badge variant="secondary">Not onboarded</Badge>
                          : <Badge variant={bc?.plan_status === 'active' ? 'default' : 'secondary'} className="capitalize">{bc?.plan_tier === 'free' ? 'Free' : bc?.plan_status || '–'}</Badge>}
                      </TableCell>
                      <TableCell className="text-sm">{bc?.category || '–'}</TableCell>
                      <TableCell className="text-sm text-center whitespace-nowrap">
                        {b.job_count > 0
                          ? <Badge variant="outline">{b.job_count}</Badge>
                          : <span className="text-muted-foreground">–</span>}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={steps.done === 3 ? 'default' : 'secondary'}
                          title={`Step 1 (Basics): ${steps.step1 ? 'completed' : 'incomplete'}\nStep 2 (Plan): ${steps.step2 ? 'completed' : 'incomplete'}\nStep 3 (Intel): ${steps.step3 ? 'completed' : 'incomplete'}`}
                        >
                          {steps.done}/3
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {bc?.is_shop_connected
                          ? <Badge variant="default" title={bc?.tiktok_seller_name || undefined}>Connected</Badge>
                          : <Badge variant="secondary">Not connected</Badge>}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {(b.subscription_start || bc?.trial_ends_at) ? (
                          <span>
                            {b.subscription_start ? shortDate(b.subscription_start) : '–'}
                            {' → '}
                            {bc?.trial_ends_at ? shortDate(bc.trial_ends_at) : '–'}
                          </span>
                        ) : '–'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{b.created_at ? shortDate(b.created_at) : '–'}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {b.intel_summary ? (
                          <div className="flex flex-col gap-1">
                            <span className="text-muted-foreground">{shortDate(b.intel_summary.last_at)}</span>
                            <Badge
                              variant={b.intel_summary.last_status === 'success' ? 'default' : 'destructive'}
                              className="w-fit text-xs capitalize"
                              title={b.intel_summary.last_status === 'failed' && b.intel_summary.last_error ? b.intel_summary.last_error : undefined}
                            >
                              {b.intel_summary.last_status}
                            </Badge>
                          </div>
                        ) : <span className="text-muted-foreground">–</span>}
                      </TableCell>
                      <TableCell className="text-sm text-center whitespace-nowrap">
                        {b.intel_summary ? b.intel_summary.successCount : <span className="text-muted-foreground">–</span>}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="View brand details"
                            onClick={() => setSelectedBrand(b)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!b.email}
                            title={b.email ? 'Switch as user (impersonate)' : 'No login email on file'}
                            onClick={() => setConfirmImpersonateBrand(b)}
                          >
                            <LogIn className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!b.brand_id}
                            title="Generate Intel Report"
                            onClick={() => setConfirmGenerateBrand(b)}
                          >
                            <Sparkles className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!b.brand_id}
                            title={b.brand_id ? 'Grant wallet credit' : 'No brand account yet'}
                            onClick={() => setCreditBrand({ id: b.brand_id, name: bc?.brand_name || b.brand_name })}
                          >
                            <Wallet className="h-4 w-4" />
                          </Button>
                          {/* Deduct is intentionally NOT restricted by plan_tier — a
                              free-plan brand can still hold credit that needs removing. */}
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!b.brand_id}
                            title={b.brand_id ? 'Deduct wallet credit' : 'No brand account yet'}
                            onClick={() => setDeductBrand({ id: b.brand_id, name: bc?.brand_name || b.brand_name })}
                          >
                            <TrendingDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>,
                  ];
                  if (isExpanded) {
                    rows.push(
                      <TableRow key={`${b.id}-members`} className="hover:bg-transparent">
                        <TableCell colSpan={15} className="p-0 border-b">
                          <div className="animate-in fade-in-0 slide-in-from-top-1 duration-200 bg-gradient-to-b from-muted/40 to-muted/10 px-5 py-4">
                            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                              Team Members ({b.members.length})
                            </p>
                            <MemberGrid
                              members={b.members}
                              emptyLabel={b.is_pending
                                ? "Hasn't completed onboarding yet — no brand or team set up"
                                : 'No team members yet'}
                              onImpersonate={(m) => setConfirmImpersonateMember({
                                member: m,
                                brandName: b.brand_config?.brand_name || b.brand_name || b.email || 'this brand',
                              })}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return rows;
                })}
                {!isLoading && brands?.length === 0 && (
                  <TableRow><TableCell colSpan={15} className="text-center text-muted-foreground">No brands found</TableCell></TableRow>
                )}
              </TableBody>
          </Table>
          {brands && brands.length > 0 && (
            <DataPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalBrands}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      {/* Invite Brand Modal */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Brand</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input type="email" placeholder="brand@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
              <p className="text-xs text-muted-foreground">A magic link will be sent to this email for first-time sign in.</p>
            </div>
            <div className="space-y-2">
              <Label>Initial Credits ($)</Label>
              <Input type="number" min="0" step="0.01" placeholder="e.g. 500" value={inviteCredits} onChange={(e) => setInviteCredits(e.target.value)} />
              <p className="text-xs text-muted-foreground">Optional. Credits will be added to their wallet once they sign up.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={isInviting || !inviteEmail}>
              {isInviting ? 'Sending...' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Funds Modal */}
      <Dialog open={!!walletBrand} onOpenChange={(open) => { if (!open) { setWalletBrand(null); setWalletAmount(''); setWalletReason(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Funds to Wallet</DialogTitle>
          </DialogHeader>
          {walletBrand && (
            <div className="space-y-4">
              <div className="space-y-1 text-sm">
                <p className="font-medium">{walletBrand.brand_name}</p>
                <p className="text-muted-foreground">Current balance: <span className="font-semibold">{usd(Math.round(Number(walletBrand.wallet_balance ?? 0) * 100))}</span></p>
              </div>
              <div className="space-y-2">
                <Label>Amount ($)</Label>
                <Input type="number" min="1" max="10000" step="0.01" placeholder="e.g. 500" value={walletAmount} onChange={(e) => setWalletAmount(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Reason (internal note)</Label>
                <Textarea placeholder="e.g. Initial deposit, Top-up, Promo credit" value={walletReason} onChange={(e) => setWalletReason(e.target.value)} rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setWalletBrand(null); setWalletAmount(''); setWalletReason(''); }}>Cancel</Button>
            <Button onClick={handleAddFunds} disabled={isAddingFunds || !walletAmount || Number(walletAmount) <= 0 || Number(walletAmount) > 10000}>
              {isAddingFunds ? 'Adding...' : 'Add Funds'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Intel Report Confirmation */}
      <Dialog open={!!confirmGenerateBrand} onOpenChange={(open) => { if (!open) setConfirmGenerateBrand(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Intel Report</DialogTitle>
          </DialogHeader>
          {confirmGenerateBrand && (
            <div className="text-sm text-muted-foreground">
              Generate a new intel report for <span className="font-medium text-foreground">{confirmGenerateBrand.brand_config?.brand_name || confirmGenerateBrand.brand_name || 'this brand'}</span>?
              <p className="mt-2">This runs a TikTok video analysis in the background. The report will appear on the brand's dashboard when complete.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmGenerateBrand(null)}>Cancel</Button>
            <Button onClick={handleGenerateIntelReport}>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Switch as User (Impersonation) Confirmation */}
      <Dialog open={!!confirmImpersonateBrand} onOpenChange={(open) => { if (!open && !isImpersonating) setConfirmImpersonateBrand(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Switch as user</DialogTitle>
          </DialogHeader>
          {confirmImpersonateBrand && (
            <div className="text-sm text-muted-foreground space-y-2">
              <p>
                Log in to the brand dashboard as{' '}
                <span className="font-medium text-foreground">
                  {confirmImpersonateBrand.brand_config?.brand_name || confirmImpersonateBrand.brand_name || confirmImpersonateBrand.email}
                </span>?
              </p>
              <p>
                A one-time magic link will open <span className="font-medium text-foreground">{brandPortal}</span> in a new tab,
                signed in as this brand. This action is logged to the audit trail.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmImpersonateBrand(null)} disabled={isImpersonating}>Cancel</Button>
            <Button onClick={handleImpersonate} disabled={isImpersonating}>
              <LogIn className="h-4 w-4 mr-2" />
              {isImpersonating ? 'Opening…' : 'Switch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BMC-managed confirmation — turning this ON holds every one of this
          brand's videos out of the 7-day auto-approve sweep indefinitely
          until a human decides, so it gets the same confirm-before-write
          treatment as impersonation and the credit dialogs, not a bare
          toggle. */}
      <Dialog open={!!pendingBmcManagedChange} onOpenChange={(open) => { if (!open && !isSavingBmcManaged) setPendingBmcManagedChange(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{pendingBmcManagedChange?.next ? 'Turn on BMC-managed review' : 'Turn off BMC-managed review'}</DialogTitle>
          </DialogHeader>
          {pendingBmcManagedChange && (
            <div className="text-sm text-muted-foreground space-y-2">
              {pendingBmcManagedChange.next ? (
                <p>
                  <span className="font-medium text-foreground">{pendingBmcManagedChange.name}</span>'s delivered
                  videos will stop auto-approving after 7 days. They'll wait for a human — BMC or the brand's own
                  team — to approve or reject them, indefinitely.
                </p>
              ) : (
                <p>
                  <span className="font-medium text-foreground">{pendingBmcManagedChange.name}</span> returns to the
                  normal rule: a video nobody decides on is approved automatically 7 days after delivery completes.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingBmcManagedChange(null)} disabled={isSavingBmcManaged}>Cancel</Button>
            <Button onClick={handleSetBmcManaged} disabled={isSavingBmcManaged}>
              {isSavingBmcManaged ? 'Saving…' : pendingBmcManagedChange?.next ? 'Turn on' : 'Turn off'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Switch as a TEAM MEMBER — lands with that member's own role on the
          brand, which is how an admin reproduces what they actually see. */}
      <Dialog
        open={!!confirmImpersonateMember}
        onOpenChange={(open) => { if (!open && !isImpersonatingMember) setConfirmImpersonateMember(null); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Switch as user</DialogTitle>
          </DialogHeader>
          {confirmImpersonateMember && (
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                Log in as{' '}
                <span className="font-medium text-foreground">
                  {confirmImpersonateMember.member.profile?.full_name
                    || confirmImpersonateMember.member.profile?.email
                    || 'this team member'}
                </span>
                {confirmImpersonateMember.member.profile?.full_name && confirmImpersonateMember.member.profile?.email
                  ? ` (${confirmImpersonateMember.member.profile.email})`
                  : ''}
                ?
              </p>
              <p>
                Their access to <span className="font-medium text-foreground">{confirmImpersonateMember.brandName}</span>:{' '}
                <span className="font-medium capitalize text-foreground">
                  {confirmImpersonateMember.member.role || 'member'}
                </span>
                {confirmImpersonateMember.member.is_owner ? ' · brand owner' : ''}. The dashboard applies
                their own role, so you see the brand as they see it rather than as its owner.
              </p>
              <p>
                A one-time magic link will open <span className="font-medium text-foreground">{brandPortal}</span> in
                a new tab. This action is logged to the audit trail.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmImpersonateMember(null)}
              disabled={isImpersonatingMember}
            >
              Cancel
            </Button>
            <Button onClick={handleImpersonateMember} disabled={isImpersonatingMember}>
              <LogIn className="h-4 w-4 mr-2" />
              {isImpersonatingMember ? 'Opening…' : 'Switch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Wallet Credit */}
      <GrantCreditDialog
        brand={creditBrand}
        onOpenChange={(open) => { if (!open) setCreditBrand(null); }}
        onGranted={() => queryClient.invalidateQueries({ queryKey: ['brand-users'] })}
      />

      {/* Deduct Wallet Credit */}
      <DeductCreditDialog
        brand={deductBrand}
        onOpenChange={(open) => { if (!open) setDeductBrand(null); }}
        onDeducted={() => queryClient.invalidateQueries({ queryKey: ['brand-users'] })}
      />

      {/* Brand Detail Drawer */}
      <Sheet open={!!selectedBrand} onOpenChange={(open) => {
        if (!open) {
          setSelectedBrand(null);
          setTransactionsPage(1);
        }
      }}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-2xl">
          <SheetHeader><SheetTitle>Brand Details</SheetTitle></SheetHeader>
          {selectedBrand && (() => {
            const bc = selectedBrand.brand_config ?? {};
            const fmt = (d: string | null | undefined) => d ? format(new Date(d), 'MMM d, yyyy') : '–';
            const fmtTs = (d: string | null | undefined) => d ? format(new Date(d), 'MMM d, yyyy h:mm a') : '–';
            const TagList = ({ items }: { items?: string[] | null }) =>
              items?.length ? (
                <div className="flex flex-wrap gap-1 mt-1">
                  {items.map((v, i) => <Badge key={i} variant="secondary" className="text-xs font-normal">{v}</Badge>)}
                </div>
              ) : <span className="text-muted-foreground">–</span>;

            const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
                <div className="text-sm font-medium break-words">{value ?? <span className="text-muted-foreground font-normal">–</span>}</div>
              </div>
            );

            const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground border-b pb-1">{title}</p>
                {children}
              </div>
            );

            return (
              <div className="space-y-6 mt-6 text-sm">

                {/* Identity */}
                <Section title="Brand">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Brand Name" value={bc.brand_name || selectedBrand.brand_name} />
                    <Field label="Email" value={selectedBrand.email || bc.contact_email} />
                    <Field label="Brand Type" value={bc.brand_type ? <Badge variant="outline" className="capitalize">{bc.brand_type.replace(/_/g, ' ')}</Badge> : null} />
                    <Field label="Account" value={
                      selectedBrand.is_agency_brand
                        ? (
                          <Badge variant="outline" className="gap-1 border-indigo-200 bg-indigo-50 font-semibold uppercase tracking-wide text-indigo-700 hover:bg-indigo-50">
                            <Building2 className="h-3 w-3" />
                            Agency{selectedBrand.agency_name ? ` · ${selectedBrand.agency_name}` : ''}
                          </Badge>
                        )
                        : <Badge variant="outline">Standalone Brand</Badge>
                    } />
                    <Field label="Active" value={
                      bc.is_active != null
                        ? <Badge variant={bc.is_active ? 'default' : 'secondary'}>{bc.is_active ? 'Active' : 'Inactive'}</Badge>
                        : null
                    } />
                    <Field label="BMC Max Client" value={
                      bc.is_bmc_max_client != null
                        ? <Badge variant={bc.is_bmc_max_client ? 'default' : 'outline'}>{bc.is_bmc_max_client ? 'Yes' : 'No'}</Badge>
                        : null
                    } />
                    <Field label="Onboarding" value={
                      bc.onboarding_completed != null
                        ? <Badge variant={bc.onboarding_completed ? 'default' : 'secondary'}>{bc.onboarding_completed ? 'Completed' : 'Pending'}</Badge>
                        : null
                    } />
                    <Field label="Onboarding Steps" value={(() => {
                      const s = getOnboardingSteps(bc);
                      return <Badge variant={s.done === 3 ? 'default' : 'secondary'} title={`Step 1 (Basics): ${s.step1 ? 'completed' : 'incomplete'}\nStep 2 (Plan): ${s.step2 ? 'completed' : 'incomplete'}\nStep 3 (Intel): ${s.step3 ? 'completed' : 'incomplete'}`}>{s.done}/3</Badge>;
                    })()} />
                    <Field label="Shop Connected" value={
                      bc.is_shop_connected != null
                        ? <Badge variant={bc.is_shop_connected ? 'default' : 'secondary'}>{bc.is_shop_connected ? (bc.tiktok_seller_name ? `Connected · ${bc.tiktok_seller_name}` : 'Connected') : 'Not connected'}</Badge>
                        : null
                    } />
                    {bc.logo_url && (
                      <div className="col-span-2">
                        <p className="text-xs text-muted-foreground mb-1">Logo</p>
                        <img src={bc.logo_url} alt="Brand logo" className="h-10 object-contain rounded border" />
                      </div>
                    )}
                  </div>
                </Section>

                {/* Category & Identity */}
                <Section title="Category & Identity">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Category" value={bc.category} />
                    <Field label="Brief Cadence" value={bc.brief_cadence} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Sub-categories</p>
                    <TagList items={bc.sub_categories} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Keywords</p>
                    <TagList items={bc.keywords} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Product Keywords</p>
                    <TagList items={bc.product_keywords} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Competitors</p>
                    <TagList items={bc.competitors} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Framework Constraints</p>
                    <TagList items={bc.framework_constraints} />
                  </div>
                </Section>

                {/* Products */}
                <Section title="Products">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Products</p>
                    <TagList items={bc.products} />
                  </div>
                  {bc.product_description && (
                    <Field label="Product Description" value={<span className="font-normal text-foreground">{bc.product_description}</span>} />
                  )}
                  {bc.target_audience && (
                    <Field label="Target Audience" value={<span className="font-normal text-foreground">{bc.target_audience}</span>} />
                  )}
                </Section>

                {/* Contact & Links */}
                <Section title="Contact & Links">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Contact Email" value={bc.contact_email} />
                    <Field label="Delivery Email" value={bc.delivery_email} />
                    <Field label="Website" value={bc.website_url || selectedBrand.website_url
                      ? <a href={bc.website_url || selectedBrand.website_url} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2 truncate block">{bc.website_url || selectedBrand.website_url}</a>
                      : null} />
                    <Field label="TikTok Shop URL" value={bc.tiktok_shop_url
                      ? <a href={bc.tiktok_shop_url} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2 truncate block">{bc.tiktok_shop_url}</a>
                      : null} />
                    <Field label="Seller Shop Code" value={bc.seller_shop_code || selectedBrand.tiktok_seller_code} />
                    {bc.delivery_discord_webhook && (
                      <Field label="Discord Webhook" value={<span className="font-mono text-xs truncate block">{bc.delivery_discord_webhook}</span>} />
                    )}
                  </div>
                </Section>

                {/* Plan & Billing */}
                <Section title="Plan & Billing">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Plan Tier" value={bc.plan_tier ? <Badge variant="outline" className="capitalize">{bc.plan_tier}</Badge> : null} />
                    <Field label="Plan Status" value={bc.plan_status ? <Badge variant={bc.plan_status === 'active' ? 'default' : 'secondary'} className="capitalize">{bc.plan_status}</Badge> : null} />
                    <Field label="Subscription Status" value={bc.subscription_status} />
                    <Field label="Trial Ends" value={fmt(bc.trial_ends_at)} />
                    {/* <Field label="Account Credits" value={bc.account_credit_cents != null ? usd(bc.account_credit_cents) : null} />
                    <Field label="Wallet Balance" value={selectedBrand.wallet_balance != null ? usd(Math.round(Number(selectedBrand.wallet_balance) * 100)) : null} />
                    <Field label="Monthly Budget" value={selectedBrand.monthly_budget != null ? usd(Math.round(Number(selectedBrand.monthly_budget) * 100)) : null} /> */}
                    {bc.whop_subscription_id && <Field label="Whop Sub ID" value={<span className="font-mono text-xs">{bc.whop_subscription_id}</span>} />}
                    {bc.whop_customer_id && <Field label="Whop Customer ID" value={<span className="font-mono text-xs">{bc.whop_customer_id}</span>} />}
                    {bc.whop_connected_account_id && <Field label="Whop Account ID" value={<span className="font-mono text-xs">{bc.whop_connected_account_id}</span>} />}
                  </div>
                </Section>

                {/* Wallet credit — live balance, re-read from the same
                    permission-checked context the grant dialog uses. */}
                <Section title="Wallet Credit">
                  <BrandCreditSummary
                    brandId={selectedBrand.brand_id ?? null}
                    onGrant={() => setCreditBrand({
                      id: selectedBrand.brand_id,
                      name: bc.brand_name || selectedBrand.brand_name,
                    })}
                    onDeduct={() => setDeductBrand({
                      id: selectedBrand.brand_id,
                      name: bc.brand_name || selectedBrand.brand_name,
                    })}
                  />
                </Section>


                {/* Timestamps */}
                <Section title="Timestamps">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Account Created" value={fmt(selectedBrand.created_at)} />
                    {/* <Field label="Invited At" value={fmt(selectedBrand.invited_at)} /> */}
                    <Field label="Onboarded At" value={fmt(bc.onboarded_at)} />
                    <Field label="Email Verified" value={fmt(bc.email_verified_at)} />
                    <Field label="Tour Seen" value={fmt(bc.tour_seen_at)} />
                    {/* <Field label="Last Constraints Run" value={fmtTs(bc.last_constraints_run_at)} /> */}
                    <Field label="Config Updated" value={fmtTs(bc.updated_at)} />
                  </div>
                </Section>

                {/* Wallet Transactions */}
                {walletTransactions && walletTransactions.length > 0 && (
                  <Section title="Wallet Transactions">
                    <div className="space-y-2">
                      {walletTransactions.map((t: any, i: number) => (
                        <div key={t.id ?? i} className="border rounded-md p-3 text-sm">
                          <div className="flex justify-between">
                            <span className="font-medium">{usd(Math.round(Number(t.amount) * 100))}</span>
                            <span className="text-muted-foreground">{t.created_at && format(new Date(t.created_at), 'MMM d, yyyy')}</span>
                          </div>
                          <div className="flex justify-between mt-1">
                            <Badge variant="outline" className="text-xs">{t.type}</Badge>
                            {t.notes && <span className="text-muted-foreground text-xs">{t.notes}</span>}
                          </div>
                        </div>
                      ))}
                      {totalTransactions && (
                        <DataPagination
                          currentPage={transactionsPage}
                          totalPages={totalTransactionsPages}
                          pageSize={transactionsPageSize}
                          totalItems={totalTransactions}
                          onPageChange={setTransactionsPage}
                          onPageSizeChange={(newSize) => { setTransactionsPage(1); setTransactionsPageSize(newSize); }}
                        />
                      )}
                    </div>
                  </Section>
                )}

              </div>
            );
          })()}
        </SheetContent>
      </Sheet>
    </div>
  );
}
